/**
 * Context bij een overleg-issue (mail-momentopname, bijlage, link) — API-laag.
 * Alles onder /Api/overleg (voordeur op develop, Node-doorgeefluik op main). Rechten en zichtbaarheid
 * bepaalt de server (404 voor wie het issue niet mag zien); deze laag doet alleen gebruiksgemak.
 */
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const BASE = `${BACKEND_URL}/Api/overleg`;

export const MAX_BIJLAGE_BYTES = 25 * 1024 * 1024;
export const BIJLAGE_EXTENSIES = ['pdf', 'png', 'jpg', 'jpeg', 'docx', 'xlsx'];

async function vraag(pad, opties = {}) {
  const ruw = opties.ruw !== undefined;
  const r = await authFetch(`${BASE}${pad}`, {
    method: opties.method || 'GET',
    headers: ruw ? { 'Content-Type': 'application/octet-stream' } : { 'Content-Type': 'application/json' },
    body: ruw ? opties.ruw : (opties.body !== undefined ? JSON.stringify(opties.body) : undefined),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const fout = new Error(typeof d.detail === 'string' ? d.detail : (d.error || `Fout ${r.status}`));
    fout.status = r.status;
    throw fout;
  }
  return d;
}

const qs = (p) => new URLSearchParams(Object.fromEntries(Object.entries(p).filter(([, v]) => v != null && v !== ''))).toString();

export const extensieVan = (naam) => ((naam || '').includes('.') ? naam.split('.').pop().toLowerCase() : '');

/** Lokale controle vóór het uploaden (de server controleert opnieuw, ook de inhoud). Geeft een foutzin of null. */
export function bijlageFout(file) {
  if (!file) return 'Geen bestand';
  if (!BIJLAGE_EXTENSIES.includes(extensieVan(file.name))) return `${file.name || 'Dit bestand'}: alleen pdf, png, jpg, docx of xlsx`;
  if (file.size > MAX_BIJLAGE_BYTES) return `${file.name}: maximaal 25 MB`;
  if (!file.size) return `${file.name}: leeg bestand`;
  return null;
}

/** Geplakte schermafdruk heet 'image.png' — geef hem een herkenbare naam. */
export function naamVoorPlakken(file, nu = new Date()) {
  const ext = extensieVan(file?.name) || (file?.type === 'image/jpeg' ? 'jpg' : 'png');
  if (file?.name && !/^image\.(png|jpe?g)$/i.test(file.name)) return file.name;
  const p = (n) => String(n).padStart(2, '0');
  return `Schermafdruk ${nu.getFullYear()}-${p(nu.getMonth() + 1)}-${p(nu.getDate())} ${p(nu.getHours())}.${p(nu.getMinutes())}.${p(nu.getSeconds())}.${ext}`;
}

/** Bestanden uit een plak- of sleepactie (clipboardData / dataTransfer). */
export function bestandenUit(dt) {
  if (!dt) return [];
  const uit = [];
  if (dt.files && dt.files.length) {
    for (let i = 0; i < dt.files.length; i += 1) uit.push(dt.files[i]);
    return uit;
  }
  for (const item of Array.from(dt.items || [])) {
    if (item.kind === 'file') { const f = item.getAsFile(); if (f) uit.push(f); }
  }
  return uit;
}

export const issueContextApi = {
  samenvatting: (typeId) => vraag(`/issue-context?type_id=${typeId}`),
  context: (issueId) => vraag(`/issues/${issueId}/context`),
  toelichting: (issueId, toelichting) => vraag(`/issues/${issueId}`, { method: 'PATCH', body: { toelichting } }),
  status: (issueId, status) => vraag(`/issues/${issueId}`, { method: 'PATCH', body: { status } }),
  opgelost: (typeId) => vraag(`/issues?type_id=${typeId}&status=opgelost`),
  link: (issueId, url, titel) => vraag(`/issues/${issueId}/context/link`, { method: 'POST', body: { url, titel } }),
  mailZoek: (issueId, filters) => vraag(`/issues/${issueId}/context/mail-zoek?${qs(filters || {})}`),
  mailKoppel: (issueId, berichtId) => vraag(`/issues/${issueId}/context/mail`, { method: 'POST', body: { bericht_id: berichtId } }),
  bijlage: (issueId, file, naam) => vraag(`/issues/${issueId}/context/bijlage?${qs({ naam: naam || file.name })}`, { method: 'POST', ruw: file }),
  download: (issueId, itemId, inline = false) => vraag(`/issues/${issueId}/context/${itemId}/download${inline ? '?inline=true' : ''}`),
  verwijder: (issueId, itemId) => vraag(`/issues/${issueId}/context/${itemId}`, { method: 'DELETE' }),
};

export default issueContextApi;
